import { Component, input, OnChanges, output, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { currentValid } from '../../../../shared/pipes/currentValid.pipe';
import { isInvalid } from '../../../../shared/helpers/validate';
import { dateify, dateifyOrNow } from '../../../../shared/helpers/date.helper';
import { BasicInfoObject, CenterObject, CreateBasicInfoInput, FileObject } from '@it2go/types';
import { CenterBasicInfosStore } from './center-basic-infos.store';
import { isAllowed, routeParams } from '../../../../shared/shared.global-service';
import { TableColumn } from '../../../../shared/components/kit/table/table.types';
import { CardComponent } from '../../../../../components/kit/card/card.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { DateInputComponent } from '../../../../../components/kit/input/date-input/date-input.component';
import { TextInputComponent } from '../../../../../components/kit/input/text-input/text-input.component';
import { WebInputComponent } from '../../../../../components/kit/input/text-input/web-input.component';
import { PhoneInputComponent } from '../../../../../components/kit/input/phone-input/phone-input.component';
import { EmailInputComponent } from '../../../../../components/kit/input/text-input/email-input.component';
import { InputLogoComponent } from '../../common/input-logo/input-logo.component';
import { Divider } from '@openng/optimus-ui/divider';
import { dateField } from '../../../../shared/components/kit/table/table.fields';
import { SaveButtonComponent } from '../../../../../components/kit/button/save-button.component';
import { TableComponent } from '../../../../shared/components/kit/table/table.component';

@Component({
    selector: 'app-center-basic-infos',
    templateUrl: './center-basic-infos.component.html',
    styleUrls: ['./center-basic-infos.component.scss'],
    providers: [CenterBasicInfosStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CardComponent,
        TranslatePipe,
        ButtonComponent,
        ReactiveFormsModule,
        DateInputComponent,
        TextInputComponent,
        WebInputComponent,
        PhoneInputComponent,
        EmailInputComponent,
        InputLogoComponent,
        Divider,
        SaveButtonComponent,
        TableComponent,
    ],
})
export class CenterBasicInfosComponent implements OnChanges {
    protected readonly store = inject(CenterBasicInfosStore);
    private readonly fb = inject(FormBuilder);


    public center = input<CenterObject | null>(null);

    public hideLogo = input<boolean>(false);
    public hideTable = input<boolean>(false);

    public infoCreated = output<CreateBasicInfoInput>();

    currentInfo: BasicInfoObject | null = null;

    form = this.fb.group({
        name: this.fb.control<string | null>(null, Validators.required),
        web: this.fb.control<string | null>(null, Validators.required),
        phone: this.fb.control<string | null>(null, Validators.required),
        email: this.fb.control<string | null>(null, [Validators.required, Validators.email]),
        ico: this.fb.control<string | null>(null),
        dic: this.fb.control<string | null>(null),
        validFrom: this.fb.control<Date | null>(null, Validators.required),
        systemFrom: this.fb.control<Date | null>(null),
        systemTo: this.fb.control<Date | null>(null),
    });

    protected columns: TableColumn[] = [
        { name: 'name' },
        dateField('validFrom', false, false),
        dateField('validTo', false, false),
        { name: 'phone' },
        { name: 'email' },
    ];

    changeEditTarget(target: BasicInfoObject) {
        this.currentInfo = target;

        this.form.patchValue({
            name: this.currentInfo!.name,
            web: this.currentInfo!.web,
            phone: this.currentInfo!.phone,
            email: this.currentInfo!.email,
            ico: this.currentInfo!.ico,
            dic: this.currentInfo!.dic,
            validFrom: dateifyOrNow(this.currentInfo!.validFrom),
            systemFrom: dateify(this.currentInfo!.systemFrom),
            systemTo: dateify(this.currentInfo!.systemTo),
        });
        this.form.markAsPristine();
    }

    async createInfo() {
        if (isInvalid(this.form)) return;

        let centerId = this.center()?.id
        if (!centerId) {
            const organizationId = routeParams.organizationId();
            const center = await this.store.api.createCenter(Number(organizationId));
            centerId = center.id
        }

        const basicInfo = {
            centerId,
            name: this.form.value.name || '',
            email: this.form.value.email || '',
            phone: this.form.value.phone || '',
            web: this.form.value.web || '',
            ico: this.form.value.ico || '',
            dic: this.form.value.dic || '',
            validFrom: this.form.value.validFrom?.toISOString() || null,
            systemFrom: this.form.value.systemFrom?.toISOString() || null,
            systemTo: this.form.value.systemTo?.toISOString() || null,
        }

        await this.store.api.createBasicInfo(basicInfo);

        this.infoCreated.emit(basicInfo)
    }

    updateInfo() {
        if (!this.currentInfo) return;
        this.store.api.updateBasicInfo({
            id: this.currentInfo.id,
            name: this.form.value.name || '',
            email: this.form.value.email || '',
            phone: this.form.value.phone || '',
            web: this.form.value.web || '',
            ico: this.form.value.ico || '',
            dic: this.form.value.dic || '',
            validFrom: this.form.value.validFrom?.toISOString() || null,
            systemFrom: this.form.value.systemFrom?.toISOString() || null,
            systemTo: this.form.value.systemTo?.toISOString() || null,
        });
        this.form.markAsPristine();
    }

    deleteInfo(data: BasicInfoObject) {
        this.store.api.deleteBasicInfo(data.id);
    }

    onFileUpload(file: FileObject) {
        if (this.center()) {
            this.store.api.updateCenter({
                id: this.center()!.id,
                image: file.name,
            });
        }
    }

    public ngOnChanges(): void {
        this.currentInfo = currentValid(this.center()?.basicInfos || []);
        if (!this.currentInfo) {
            this.form.patchValue({
                validFrom: new Date(),
            });
            return;
        }

        this.form.patchValue({
            name: this.currentInfo!.name,
            web: this.currentInfo!.web,
            phone: this.currentInfo!.phone,
            email: this.currentInfo!.email,
            ico: this.currentInfo!.ico,
            dic: this.currentInfo!.dic,
            validFrom: dateifyOrNow(this.currentInfo!.validFrom),
            systemFrom: dateify(this.currentInfo!.systemFrom),
            systemTo: dateify(this.currentInfo!.systemTo),
        });
        this.form.markAsPristine();
    }

    protected readonly isAllowed = isAllowed;
}
